import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Crown } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

export default function UpgradeModal({ isOpen, onClose, limitInfo }) {
  const { planId, startCheckout } = useSubscription();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (targetPlan) => {
    if (!currentUser || currentUser.isAnonymous) {
      alert('Please sign in with Google or Apple to subscribe.');
      return;
    }
    setLoading(targetPlan);
    try {
      await startCheckout(targetPlan);
    } catch (err) {
      console.error('Checkout failed:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const isFree = planId === 'free';
  const limitType = limitInfo?.limitType || 'generation';
  const used = limitInfo?.generationsUsed || limitInfo?.drawingsUsed || limitInfo?.messagesUsed || 0;
  const limit = limitInfo?.generationsLimit || limitInfo?.drawingsLimit || limitInfo?.messagesLimit || 5;

  // Determine emoji, title, and subtitle based on limit type
  let emoji = '🎨';
  let title = "You've used your free generations!";
  let subtitle = `You've created ${used} of ${limit} free artworks. Upgrade to keep creating!`;
  let dismissText = 'Continue chatting with Ross (no new art)';

  if (limitType === 'drawing') {
    emoji = '✏️';
    title = "You've used all your free drawings!";
    subtitle = isFree
      ? `You've drawn ${used} of ${limit} times this month. Upgrade to draw unlimited!`
      : `You've used all ${limit} drawings this month. They'll refresh on your next billing cycle.`;
    dismissText = 'Continue with AI generations only';
  } else if (limitType === 'message') {
    emoji = '💬';
    title = "You've sent all your free messages!";
    subtitle = isFree
      ? `You've sent ${used} of ${limit} messages this month. Upgrade to chat unlimited with Ross!`
      : `You've used all ${limit} messages this month. They'll refresh on your next billing cycle.`;
    dismissText = 'Continue without messaging';
  } else if (limitType === 'style_transfer') {
    emoji = '🎨';
    title = 'Style transfer is premium!';
    subtitle = 'Transform your art with different styles by upgrading to Family, Family Plus, or Family Pro. Free users can still draw, chat with Ross, and generate AI art!';
    dismissText = 'Continue without style transfer';
  } else if (!isFree) {
    // Paid user hit generation limit
    title = 'Generation limit reached';
    subtitle = `You've used all ${limit} generations this month. Upgrade for more creative freedom.`;
    dismissText = 'Keep chatting without new art';
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Modal
            as={motion.div}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>

            <Emoji>{emoji}</Emoji>
            <ModalTitle>{title}</ModalTitle>
            <ModalSubtitle>{subtitle}</ModalSubtitle>

            <PlanOptions>
              {isFree && (
                <PlanOption onClick={() => handleUpgrade('family')} disabled={loading === 'family'}>
                  <PlanLeft>
                    <IconWrap><Sparkles size={18} /></IconWrap>
                    <div>
                      <PlanLabel>Family</PlanLabel>
                      <PlanDetail>200 AI art + unlimited drawing/chat</PlanDetail>
                    </div>
                  </PlanLeft>
                  <PlanPrice>{loading === 'family' ? '...' : '$9.99/mo'}</PlanPrice>
                </PlanOption>
              )}

              <PlanOption $highlight onClick={() => handleUpgrade('family_plus')} disabled={loading === 'family_plus'}>
                <PlanLeft>
                  <IconWrap $highlight><Crown size={18} /></IconWrap>
                  <div>
                    <PlanLabel>Family Plus</PlanLabel>
                    <PlanDetail>400 AI art + unlimited + style transfer</PlanDetail>
                  </div>
                </PlanLeft>
                <PlanPrice>{loading === 'family_plus' ? '...' : '$19.99/mo'}</PlanPrice>
              </PlanOption>
            </PlanOptions>

            <DismissLink onClick={onClose}>{dismissText}</DismissLink>
          </Modal>
        </>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
`;

const Modal = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 28px 20px max(20px, env(safe-area-inset-bottom));
  z-index: 201;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 6px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textTertiary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const Emoji = styled.div`
  font-size: 40px;
  text-align: center;
  margin-bottom: 8px;
`;

const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 6px;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 24px;
  line-height: 1.4;
`;

const PlanOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const PlanOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  border: 2px solid ${({ $highlight, theme }) => ($highlight ? theme.colors.accent : theme.colors.border)};
  background: ${({ $highlight }) => ($highlight ? '#fffbeb' : 'white')};
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const PlanLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $highlight, theme }) => ($highlight ? theme.colors.accent : theme.colors.surface)};
  color: ${({ $highlight }) => ($highlight ? 'white' : '#333')};
`;

const PlanLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  text-align: left;
`;

const PlanDetail = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: left;
`;

const PlanPrice = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
`;

const DismissLink = styled.button`
  display: block;
  width: 100%;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textTertiary};
  padding: 8px;

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;
